<script setup lang="ts">
  import OrdersService from '@/api/services/orders.service';
  import CompleteTime from '@/components/CompleteTime.vue';
  import { useOrders } from '@/composition/use-orders';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import { ORDER_TYPES_MAP } from '@/const/orders.const';
  import { OrderStatus, OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { orderItemsToText } from '@/utils/orders.utils';
  import { computed, ref } from 'vue';

  const props = defineProps<{
    order: IOrder;
  }>();

  const orderStore = useOrdersStore();
  const orderHelper = useOrders();
  const ui = useUi();
  const modals = useModal();

  const restaurantStore = useRestaurantStore();

  const orderType = computed(() => ORDER_TYPES_MAP.get(props.order.type));

  const orderItemsText = computed(() =>
    orderItemsToText({
      order: props.order,
      maxItems: 5,
    })
  );

  function openOrder() {
    orderHelper.openOrder(props.order);
  }

  const updating = ref(false);
  async function setStatus(status: OrderStatus) {
    if (updating.value) return;

    try {
      updating.value = true;
      const didUpdate = await orderHelper.setOrderStatus(props.order, status);
      if (!didUpdate) return;

      const action = {
        text: 'Ver pedido',
        cb: () => orderHelper.openOrder(props.order),
      };

      ui.showToast(AlertType.Success, 'Estado actualizado', action);
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el estado');
    } finally {
      updating.value = false;
    }
  }

  async function rejectOrder() {
    modals
      .confirm({
        title: 'Rechazar pedido',
        message: '¿Está seguro de que desea rechazar este pedido?',
      })
      .then((confirmed) => {
        if (!confirmed) return;

        setStatus(OrderStatus.Declined);
      });
  }

  const isUnseen = computed(() => {
    return orderStore.unseenOrderIdsSet.has(props.order.id);
  });

  function setSeen() {
    orderStore.removeUnseenOrder(props.order.id);
  }
</script>

<template>
  <div
    class="new-order"
    :class="{
      unseen: isUnseen,
    }"
    @pointerenter="setSeen"
  >
    <div class="header">
      <v-chip class="order-type" small>
        <v-icon class="pr-1" small>{{ orderType.icon }}</v-icon>
        {{ orderType.title }}
      </v-chip>
      <span class="order-total">
        {{ restaurantStore.currency?.symbol }} {{ displayPrice(order.total) }}
      </span>
    </div>
    <div class="body">
      <template v-if="order.type === OrderType.Seat">
        <div class="info-line">
          <v-icon small>mdi-table-furniture</v-icon>
          <span>Mesa {{ order.table?.name }}</span>
        </div>
      </template>
      <template v-else-if="order.type === OrderType.Delivery">
        <div class="info-line">
          <v-icon small>mdi-map-marker</v-icon>
          <span>{{ order.userAddress }}</span>
        </div>
      </template>
      <template v-else-if="order.type === OrderType.Away">
        <div class="info-line">
          <!-- Order id -->
          <v-icon small>mdi-receipt</v-icon>
          <span>#{{ order.humanId }}</span>
        </div>
      </template>
      <div class="info-line">
        <v-icon v-if="order.userName" small>mdi-account</v-icon>
        <span>{{ order.userName }}</span>
      </div>
      <!-- Order items -->
      <div class="info-line">
        <v-icon small>mdi-cart</v-icon>
        <span>{{ orderItemsText }}</span>
      </div>
    </div>
    <div class="footer">
      <CompleteTime :date="order.createdAt" class="time" />
      <v-btn
        class="action-btn view"
        color="primary"
        x-small
        fab
        @click="openOrder"
      >
        <v-icon>mdi-eye</v-icon>
      </v-btn>
      <v-btn
        class="action-btn reject"
        color="error"
        x-small
        fab
        :loading="updating"
        @click="rejectOrder"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-btn
        class="action-btn accept"
        color="success"
        x-small
        fab
        :loading="updating"
        @click="setStatus(OrderStatus.Progress)"
      >
        <v-icon>mdi-check</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @keyframes unseen-pulse {
    0%,
    100% {
      @apply tw-bg-orange-50;
    }
    50% {
      @apply tw-bg-orange-100;
    }
  }

  .new-order {
    @apply tw-border tw-border-gray-300 tw-rounded-xl tw-py-2 tw-divide-y tw-divide-dashed;

    .header {
      @apply tw-flex tw-items-center tw-justify-between tw-space-x-2 tw-pb-1.5 tw-px-2;

      .order-total {
        @apply tw-font-semibold tw-text-black;
      }
    }

    .body {
      @apply tw-space-y-1 tw-py-1.5 tw-px-2;

      .info-line {
        @apply tw-flex tw-items-start tw-space-x-1 tw-text-black;
        line-height: 1.2em;
      }
    }

    .footer {
      @apply tw-flex tw-justify-end tw-space-x-2 tw-items-center tw-pt-1.5 tw-px-2;

      .time {
        @apply tw-flex-1;
      }

      .action-btn {
        @apply tw-shadow-none tw-rounded-lg;
      }
    }

    &:hover {
      @apply tw-bg-gray-50;
    }

    &.unseen {
      @apply tw-border-orange-300 tw-divide-orange-200;
      animation: unseen-pulse 1s ease-in-out infinite;
    }
  }
</style>
